import { useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import type { Customer, CustomerMeter } from '@metron/shared';
import { useTableUrlState } from '../../hooks/useTableUrlState.ts';
import { type MeterScope, useCustomerMeters } from '../../hooks/useCustomerMeters.ts';
import { useProductTypeFilters } from '../../hooks/useProductTypeFilters.ts';
import { ProductTypeFilterBar } from '../ProductTypeFilterBar';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card.tsx';
import { DataTable } from '../data-table/data-table.tsx';
import { DataTableColumnHeader } from '../data-table/data-table-column-header.tsx';
import { Button } from '../ui/button.tsx';
import { InstalledMetersTooltip } from '../InstalledMetersTooltip.tsx';

const meterColumns: ColumnDef<CustomerMeter>[] = [
  {
    accessorKey: 'deviceId',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Device ID' />,
    cell: ({ row }) => <div className='font-medium'>{row.getValue('deviceId') as string}</div>,
  },
  {
    accessorKey: 'address',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Address' />,
    cell: ({ row }) => {
      const value = row.getValue('address') as string | null;
      return value || <span className='text-muted-foreground'>N/A</span>;
    },
  },
  {
    accessorKey: 'consumerName',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Consumer Name' />,
    cell: ({ row }) => {
      const value = row.getValue('consumerName') as string | null;
      return value || <span className='text-muted-foreground'>N/A</span>;
    },
  },
  {
    accessorKey: 'deviceDescription',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Device Type' />,
    cell: ({ row }) => {
      const value = row.getValue('deviceDescription') as string | null;
      return value || <span className='text-muted-foreground'>N/A</span>;
    },
  },
  {
    accessorKey: 'rate',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Rate' />,
    cell: ({ row }) => {
      const raw = row.getValue('rate');
      if (raw == null) return <span className='text-muted-foreground'>N/A</span>;
      return <div className='text-right'>{Number(raw).toFixed(2)}</div>;
    },
  },
  {
    accessorKey: 'lastReadDate',
    header: ({ column }) => <DataTableColumnHeader column={column} title='Last Read' />,
    cell: ({ row }) => {
      const value = row.getValue('lastReadDate') as string | null;
      if (!value) return <span className='text-muted-foreground'>N/A</span>;
      return new Date(value).toLocaleString();
    },
  },
];

/**
 * Meters tab of the customer details page: the customer's meters from their
 * all_sensors_group. It owns the unprefixed `page` / `pageSize` / `sortBy` /
 * `sortDir` keys plus `scope`.
 */
export function CustomerMetersTab({ customer }: { customer: Customer }) {
  const navigate = useNavigate();
  const customerId = String(customer.id);

  const { pagination, sorting, onPaginationChange, onSortingChange } = useTableUrlState({
    defaultPageSize: 50,
  });

  const sortBy = sorting.length > 0 ? sorting[0].id : undefined;
  const sortOrder = sorting.length > 0 ? (sorting[0].desc ? 'desc' : 'asc') : undefined;

  const { registerBuckets, meterBuckets, setRegister, setMeter } = useProductTypeFilters();

  const [searchParams, setSearchParams] = useSearchParams();
  const scope: MeterScope = searchParams.get('scope') === 'all' ? 'all' : 'installed';
  const setScope = useCallback(
    (next: MeterScope) => {
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          if (next === 'installed') {
            params.delete('scope');
          } else {
            params.set('scope', next);
          }
          // Reset pagination on scope change — the underlying row set changes.
          params.delete('page');
          return params;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const { data: metersData, isLoading: metersLoading, error: metersError } = useCustomerMeters({
    customerId,
    page: pagination.pageIndex + 1, // API uses 1-based pages
    limit: pagination.pageSize,
    sortBy,
    sortOrder,
    scope,
    registerBuckets,
    meterBuckets,
  });

  // Unconstrained baselines for "Showing N of M": one per scope, so the
  // denominator matches the toggle. limit:1 keeps the payload tiny — we
  // only consume `total`.
  const { data: installedBaseline } = useCustomerMeters({
    customerId,
    page: 1,
    limit: 1,
    scope: 'installed',
  });
  const { data: allBaseline } = useCustomerMeters({
    customerId,
    page: 1,
    limit: 1,
    scope: 'all',
  });
  const baselineTotal = scope === 'installed' ? installedBaseline?.total : allBaseline?.total;

  const handleMeterRowClick = (meter: CustomerMeter) => {
    navigate(`/meter-inspector/${meter.sensorId}`);
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Meters</CardTitle>
        <CardDescription>
          <InstalledMetersTooltip>
            {scope === 'installed' ? 'Installed' : 'All assigned'}
          </InstalledMetersTooltip>{' '}
          meters for this customer. Click a row to view meter details.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className='flex items-center justify-between gap-2 mb-3 flex-wrap'>
          <div
            role='group'
            aria-label='Meter scope'
            className='inline-flex rounded-md border border-input bg-background p-0.5'
          >
            <Button
              type='button'
              variant={scope === 'installed' ? 'default' : 'ghost'}
              size='sm'
              aria-pressed={scope === 'installed'}
              onClick={() => setScope('installed')}
            >
              Installed ({(installedBaseline?.total ?? 0).toLocaleString()})
            </Button>
            <Button
              type='button'
              variant={scope === 'all' ? 'default' : 'ghost'}
              size='sm'
              aria-pressed={scope === 'all'}
              onClick={() => setScope('all')}
            >
              All ({(allBaseline?.total ?? customer.sensorCount).toLocaleString()})
            </Button>
          </div>
        </div>
        <ProductTypeFilterBar
          registerBuckets={registerBuckets}
          meterBuckets={meterBuckets}
          onRegisterChange={setRegister}
          onMeterChange={setMeter}
          trailing={
            <p className='text-sm text-muted-foreground'>
              Showing {(metersData?.total ?? 0).toLocaleString()} of{' '}
              {(baselineTotal ?? customer.sensorCount).toLocaleString()}{' '}
              {scope === 'installed' ? 'installed' : ''} meters
            </p>
          }
        />
        <div className='mt-4'>
          {metersLoading && !metersData
            ? (
              <p className='text-muted-foreground text-sm py-4 text-center'>
                Loading meters...
              </p>
            )
            : metersError
            ? (
              <p className='text-destructive text-sm py-4 text-center'>
                Failed to load meters
              </p>
            )
            : (metersData?.meters.length ?? 0) === 0 &&
                (registerBuckets.length > 0 || meterBuckets.length > 0)
            ? (
              <p className='text-muted-foreground text-sm py-4 text-center'>
                No meters match the selected filters.
              </p>
            )
            : (
              <DataTable
                columns={meterColumns}
                data={metersData?.meters ?? []}
                onRowClick={handleMeterRowClick}
                manualPagination
                rowCount={metersData?.total ?? 0}
                pagination={pagination}
                onPaginationChange={onPaginationChange}
                manualSorting
                sorting={sorting}
                onSortingChange={onSortingChange}
              />
            )}
        </div>
      </CardContent>
    </Card>
  );
}
